import { freelancerUser } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Freelancer User Service
 * Handles all freelancer/business user application admin API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/freelancer-user/list - Get freelancer user list
 * - GET /api/v1/admin/freelancer-user/get - Get single freelancer user detail
 * - PUT /api/v1/admin/freelancer-user/approve-application - Approve application
 * - PUT /api/v1/admin/freelancer-user/reject-application - Reject application
 * - PUT /api/v1/admin/freelancer-user/approve-documents - Approve verification documents
 * - PUT /api/v1/admin/freelancer-user/reject-documents - Reject verification documents
 * - PUT /api/v1/admin/freelancer-user/approve-service - Approve an individual service
 * - PUT /api/v1/admin/freelancer-user/reject-service - Reject an individual service
 */
class FreelancerUserService {
  /**
   * @name getList
   * @description Fetches freelancer user list with pagination, search and filters.
   * @param {Object} params - Query parameters (page, limit, search, application_status, verification_status, service_status, profession_id, date_from, date_to)
   * @returns {Promise<Object>} The response data containing the freelancer user list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(freelancerUser.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single freelancer user's full profile detail by UUID.
   * @param {string} uuid - Freelancer profile UUID
   * @returns {Promise<Object>} The response data containing the freelancer user detail.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(freelancerUser.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name approveApplication
   * @description Approves a freelancer's application.
   * @param {string} uuid - Freelancer profile UUID
   * @returns {Promise<Object>} The response data.
   */
  static async approveApplication(uuid) {
    try {
      const response = await axios.put(freelancerUser.approveApplication, { uuid });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name rejectApplication
   * @description Rejects a freelancer's application with a reason.
   * @param {string} uuid - Freelancer profile UUID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} The response data.
   */
  static async rejectApplication(uuid, reason) {
    try {
      const response = await axios.put(freelancerUser.rejectApplication, { uuid, reason });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name approveDocuments
   * @description Approves a freelancer's uploaded verification documents.
   * @param {string} uuid - Freelancer profile UUID
   * @returns {Promise<Object>} The response data.
   */
  static async approveDocuments(uuid) {
    try {
      const response = await axios.put(freelancerUser.approveDocuments, { uuid });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name rejectDocuments
   * @description Rejects a freelancer's uploaded verification documents with a reason.
   * @param {string} uuid - Freelancer profile UUID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} The response data.
   */
  static async rejectDocuments(uuid, reason) {
    try {
      const response = await axios.put(freelancerUser.rejectDocuments, { uuid, reason });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name approveService
   * @description Approves one of a freelancer's individual services.
   * @param {string} uuid - Freelancer service UUID
   * @returns {Promise<Object>} The response data.
   */
  static async approveService(uuid) {
    try {
      const response = await axios.put(freelancerUser.approveService, { uuid });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name rejectService
   * @description Rejects one of a freelancer's individual services.
   * @param {string} uuid - Freelancer service UUID
   * @returns {Promise<Object>} The response data.
   */
  static async rejectService(uuid) {
    try {
      const response = await axios.put(freelancerUser.rejectService, { uuid });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name grantPremiumReward
   * @description Grants the configured number of months of free premium to the given freelancer users.
   * @param {string[]} uuids - Freelancer profile UUIDs to grant the reward to
   * @returns {Promise<Object>} The response data, whose `data` includes { granted_count, months }.
   */
  static async grantPremiumReward(uuids) {
    try {
      const response = await axios.post(freelancerUser.grantPremiumReward, { freelancer_uuids: uuids });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { FreelancerUserService };
